import { observer } from 'mobx-react-lite';
import React, { createContext, PropsWithChildren, useContext } from 'react';
import { Card, CardBody, CardHeader, Col, Container, Row, Spinner } from 'reactstrap';
import GlobalStore from './GlobalStore';
import CertificateReportStore from './CertificateReportStore';
import PortfolioStore from './PortfolioStore';

export interface Stores {
    globalStore: GlobalStore
    certificateReportStore: CertificateReportStore
    portfolioStore: PortfolioStore
}

export const storesContext = createContext<Stores | undefined>(undefined);

/** A React Hook to use the app stores inside a provider */
export const useStores = () => {
    const stores = useContext(storesContext);
    if (!stores) {
        throw new Error('useStores must be used within a StoreProvider.');
    }
    return stores;
};

const LoginBlock = observer(({ children }: PropsWithChildren<unknown>) => {
    const { globalStore } = useStores();

    globalStore.initialiseAsync();

    if (globalStore.error) {
        throw globalStore.error;
    }

    if (globalStore.userInfo === undefined || globalStore.loggingIn) {
        return <Container>
            <Row>
                <Col sm={4} />
                <Col sm={4}>
                    <Card className='m-3'>
                        <CardHeader>
                            {globalStore.loggingIn ? 'Redirecting to login...' : 'Checking if you\'re logged in...'}
                        </CardHeader>
                        <CardBody>
                            <Spinner color='primary' />
                        </CardBody>
                    </Card>
                </Col>
                <Col sm={4} />
            </Row>
        </Container>;
    }

    return <>{children}</>;
});

const StoreProvider = ({ children }: PropsWithChildren<unknown>) => {
    const globalStore = new GlobalStore();
    const stores = {
        globalStore,
        certificateReportStore: new CertificateReportStore(globalStore),
        portfolioStore: new PortfolioStore(globalStore)
    };
    return (<>
        <storesContext.Provider value={stores}>
            <LoginBlock>
                {children}
            </LoginBlock>
        </storesContext.Provider>
    </>);
};

export default StoreProvider;
